/**
 * Where a Bilanz (`file.file_type = 'Bilanz'`) lives and how it is served.
 *
 * German statements are register HTML in the unified `firmendata-bilanz`
 * bucket and are rendered in-page. Austrian Jahresabschlüsse
 * (`file.origin = 'firmenbuch'`, see backend `backend/firmenbuch/storage.py`
 * and `storage_utils.get_bucket_for_file`) are the Firmenbuch's own XML or
 * PDF in `firmendata-at-firmenbuch-urkunden`, keyed `<fn>/<URKID>.<ext>`;
 * they are never rendered as HTML. Show the PDF, parse the XML: the content
 * route serves the statement's PDF rendition inline when one exists (for an
 * XML statement the backend fetches its Firmenbuch PDF twin once and stores
 * it as `<fn>/<URKID>.twin.pdf`), and the clients open it in a new tab;
 * otherwise the stored file goes out as an attachment and the clients save
 * it. An XML is never served inline.
 *
 * Imported by both the route handlers and the client components; the
 * functions that touch `window`/`document` are browser only.
 */

/** `file.origin` of every Austrian Firmenbuch Jahresabschluss row. */
export const FIRMENBUCH_ORIGIN = 'firmenbuch';

/** Unified bucket of the German (Bundesanzeiger / Unternehmensregister) Bilanz HTML. */
export const BILANZ_BUCKET = 'firmendata-bilanz';

/** FileOps `at_firmenbuch_urkunden` — Austrian Jahresabschlüsse (XML / PDF). */
export const AT_FIRMENBUCH_URKUNDEN_BUCKET = 'firmendata-at-firmenbuch-urkunden';

export function isFirmenbuchFile(origin: string | null | undefined): boolean {
  return origin === FIRMENBUCH_ORIGIN;
}

/** Bucket of a Bilanz file row, decided by `file.origin`. */
export function bilanzBucket(origin: string | null | undefined): string {
  return isFirmenbuchFile(origin) ? AT_FIRMENBUCH_URKUNDEN_BUCKET : BILANZ_BUCKET;
}

const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  xml: 'application/xml',
  pdf: 'application/pdf',
};

function keyExtension(key: string): string | null {
  const match = /\.([a-z0-9]+)$/i.exec(key);
  return match ? match[1].toLowerCase() : null;
}

/**
 * Content type of a Firmenbuch document: the one stored on the object
 * (`store_urkunde` writes `application/xml` / `application/pdf`), else the
 * key's extension. Never `text/html` — an Austrian XML rendered as HTML
 * would be both wrong and an injection surface.
 */
export function firmenbuchContentType(
  key: string,
  stored?: string | null,
): string {
  const storedType = stored?.split(';')[0].trim().toLowerCase();
  if (storedType && storedType !== 'application/octet-stream' && !storedType.includes('html')) {
    return storedType;
  }
  const ext = keyExtension(key);
  return (ext && EXTENSION_CONTENT_TYPES[ext]) || 'application/octet-stream';
}

/** Download file name: the public name plus the stored object's extension. */
export function firmenbuchFileName(
  publicName: string | null | undefined,
  key: string,
  fileId: number,
): string {
  const ext = keyExtension(key);
  const base =
    (publicName || `Jahresabschluss-${fileId}`)
      .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 150) || `Jahresabschluss-${fileId}`;
  if (!ext || base.toLowerCase().endsWith(`.${ext}`)) return base;
  return `${base}.${ext}`;
}

/** `Content-Disposition: attachment` with an ASCII fallback and the UTF-8 name. */
export function attachmentDisposition(fileName: string): string {
  const ascii = fileName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7e]/g, '_')
    .replace(/["\\]/g, '_');
  return `attachment; filename="${ascii}"; filename*=UTF-8''${encodeURIComponent(fileName)}`;
}

/** True when the content route answered with a file to save, not HTML to render. */
export function isAttachmentResponse(response: Response): boolean {
  return /^\s*attachment/i.test(response.headers.get('Content-Disposition') ?? '');
}

function dispositionFileName(header: string | null): string | null {
  if (!header) return null;
  const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // fall through to the plain filename
    }
  }
  const plain = /filename\s*=\s*"([^"]*)"/i.exec(header);
  return plain ? plain[1] : null;
}

/** Save an attachment response through a Blob + anchor click (browser only). */
export async function saveResponseAsFile(
  response: Response,
  fallbackName: string,
): Promise<void> {
  const blob = await response.blob();
  const name =
    dispositionFileName(response.headers.get('Content-Disposition')) ||
    fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick: some browsers start the download asynchronously.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * `Content-Disposition: inline` with the same ASCII fallback and UTF-8 name
 * as {@link attachmentDisposition}. Only ever used for a PDF — an XML is
 * always an attachment, never shown inline.
 */
export function inlineDisposition(fileName: string): string {
  return attachmentDisposition(fileName).replace(/^attachment/, 'inline');
}

/**
 * Show the PDF, parse the XML: the backend's answer to "which PDF shows this
 * Austrian statement" (`GET /public/web/firmenbuch/files/{id}/pdf`, backend
 * `backend/firmenbuch/pdf_twin.py`). Accepted only for the Firmenbuch bucket
 * and a `<fn>/<URKID>[.twin].pdf` key, so a malformed answer can never make
 * the content route serve some other object inline.
 */
export function firmenbuchPdfKey(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const {
    bucket,
    key,
    content_type: contentType,
  } = payload as { bucket?: unknown; key?: unknown; content_type?: unknown };
  if (bucket !== AT_FIRMENBUCH_URKUNDEN_BUCKET) return null;
  if (contentType !== 'application/pdf') return null;
  if (typeof key !== 'string' || !/^\d{1,6}\/\d+(\.twin)?\.pdf$/i.test(key)) {
    return null;
  }
  return key;
}

/** True when the content route answered with a PDF to show, not to save. */
export function isInlinePdfResponse(response: Response): boolean {
  const type = (response.headers.get('Content-Type') ?? '')
    .split(';')[0]
    .trim()
    .toLowerCase();
  return (
    type === 'application/pdf' &&
    /^\s*inline/i.test(response.headers.get('Content-Disposition') ?? '')
  );
}

/**
 * Hand an Austrian statement from the content route to the browser
 * (browser only). Returns false for any other response (German HTML), which
 * the caller renders as before.
 *
 * - inline PDF → shown in `tab`, the placeholder the click handler opened
 *   before its first await (lib/document-tab.ts: once the fetch is back the
 *   user gesture is gone and `window.open` is blocked). The bytes become a
 *   blob URL in the page's own origin, so auth and billing stay inside the
 *   one fetch that already happened. Without a tab: one more `window.open`
 *   try, else the PDF is saved like an attachment.
 * - attachment (the XML, when no PDF rendition exists) → saved as a file
 *   and the placeholder tab closed.
 */
export async function deliverFirmenbuchResponse(
  response: Response,
  tab: Window | null,
  fallbackName: string,
): Promise<boolean> {
  if (isInlinePdfResponse(response)) {
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    // Long enough for the viewer to load and for "save" inside it.
    window.setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
    if (tab && !tab.closed) {
      tab.location.href = url;
      return true;
    }
    if (window.open(url, '_blank')) return true;
    const a = document.createElement('a');
    a.href = url;
    a.download =
      dispositionFileName(response.headers.get('Content-Disposition')) ||
      fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    return true;
  }
  tab?.close();
  if (isAttachmentResponse(response)) {
    await saveResponseAsFile(response, fallbackName);
    return true;
  }
  return false;
}
